import type { FC, ReactNode } from "react";
import React, { useMemo, useState, useCallback } from "react";
import {
  HiSearch,
  HiBell,
  HiViewGrid,
  HiUserGroup,
  HiDesktopComputer,
  HiUser,
  HiUsers,
  HiPlus,
} from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import Global from "../../globals/globals";
import type { ChatRoom } from "@/types/chat.types";

const ROOM_TYPES = {
  all: { label: "All", icon: HiViewGrid, color: "text-purple-600" },
  "agent-to-agent": { label: "Agent", icon: HiDesktopComputer, color: "text-pink-600" },
  "c2a": { label: "Client", icon: HiUser, color: "text-green-600" },
  "group-chat": { label: "Group", icon: HiUserGroup, color: "text-yellow-600" },
  conference: { label: "Conference", icon: HiUsers, color: "text-blue-600" },
  unread: { label: "Unread", icon: HiBell, color: "text-red-600" },
} as const;

type RoomTypeKey = keyof typeof ROOM_TYPES;

const formatTimestamp = (unixTsString: any): string => {
  if (!unixTsString) return "";
  const n = Number(unixTsString);
  if (!Number.isFinite(n)) return "";
  const ms = n > 9_999_999_999 ? n : n * 1000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

function mapSessionTypeToUiType(session_type_id: number): Exclude<RoomTypeKey, "all" | "unread"> {
  switch (session_type_id) {
    case 0: return "c2a";
    case 1: return "c2a";
    case 2: return "conference";
    case 3: return "group-chat";
    case 4: return "agent-to-agent";
    default: return "c2a";
  }
}

function getDisplayNameFromSessionKey(session_key: string, uiType: string, agent?: any, client?: any, participants?: any[]): string {
  if (uiType === "group-chat") {
    if (session_key.startsWith("grpcht:")) {
      const groupName = session_key.substring(7);
      return groupName || session_key;
    }
    const parts = session_key.split("+").map((p) => p.split("@")[0]).filter(Boolean);
    return parts.join(", ") || session_key;
  }

  if (uiType === "conference") {
    if (session_key.startsWith("conf:")) {
      const confName = session_key.substring(5);
      return confName || session_key;
    }
    const parts = session_key.split("+").map((p) => p.split("@")[0]).filter(Boolean);
    return parts.join(", ") || session_key;
  }

  if (uiType === "c2a" && client?.client_username) return client.client_username;

  if (uiType === "agent-to-agent") {
    // For agent-to-agent, find the receiver from participants where is_current_user is false
    if (participants && participants.length > 0) {
      const receiver = participants.find(p => p.is_current_user === false);
      if (receiver?.details?.agent_username) {
        return receiver.details.agent_username;
      }
    }
    // Fallback: extract from session_key (e.g., "a2a:user1@domain+user2@domain")
    const parts = session_key.split("+").map((p) => p.split("@")[0]).filter(Boolean);
    return parts[0] || session_key;
  }

  const parts = session_key.split("+").map((p) => p.split("@")[0]).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]} ↔ ${parts[1]}`;
  return parts[0] || session_key;
}

interface RoomRowProps {
  room: ChatRoom;
  isActive: boolean;
  onClick: () => void;
  index: number;
  lastMessage?: string;
}

const RoomRow: FC<RoomRowProps> = ({ room, isActive, onClick, index, lastMessage = "No messages yet" }) => {
  const sessionKey = room.session_key as string;
  const uiType = mapSessionTypeToUiType(room.session_type_id);
  const displayName = getDisplayNameFromSessionKey(sessionKey, uiType, room?.agent, room?.client, room?.participants);
  const timeTs = room.created_at;
  const unreadCount = room.unread_count || 0;
  const isSessionActive = room.session_status === "active";

  return (
    <div
      onClick={onClick}
      className={`
        relative p-2.5 rounded-xl cursor-pointer transition-all duration-200 group
        ${
          isActive
            ? "bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 border border-blue-200 dark:border-blue-700 shadow-md"
            : "bg-white/80 dark:bg-gray-700/80 hover:bg-white dark:hover:bg-gray-700 border border-gray-200/50 dark:border-gray-600/50 hover:shadow-md backdrop-blur-sm"
        }
      `}
    >
      <div className="flex items-center gap-2.5">
        <div className="flex-shrink-0 relative">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-sm">
            <span className="text-white font-medium text-xs">
              {displayName?.charAt(0)?.toUpperCase() || "?"}
            </span>
          </div>
          {isSessionActive && (
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-0.5">
            <h4
              className={`text-sm truncate ${
                unreadCount > 0 ? "font-bold text-gray-900 dark:text-white" : "font-medium text-gray-900 dark:text-white"
              }`}
            >
              {displayName}
            </h4>

            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                {formatTimestamp(timeTs)}
              </span>

              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs rounded-full font-medium min-w-[18px] text-center">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs truncate flex-1 text-gray-600 dark:text-gray-400">
              <span>{lastMessage}</span>
            </div>
          </div>

          <div className="mt-1">
            <span
              className={`
                inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium
                ${ROOM_TYPES[uiType]?.color || "text-gray-600"}
              `}
            >
              {React.createElement(ROOM_TYPES[uiType]?.icon || HiUser, {
                className: "w-2.5 h-2.5",
              })}
              {ROOM_TYPES[uiType]?.label || uiType}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ConversationsSidebarContainer - PARENT COMPONENT
 *
 * This is the main parent component that manages:
 * 1. Room list display (sidebar panel)
 * 2. Selected room state
 * 3. Layout (sidebar + chat area)
 * 4. Renders ChatArea as children when room is selected
 *
 * Architecture:
 * ConversationsSidebarContainer (Parent)
 * ├── Sidebar Panel (room list)
 * └── children (ChatArea or custom content)
 *
 * Usage:
 * <ConversationsSidebarContainer
 *   rooms={rooms}
 *   onRoomSelect={(room) => console.log('Selected:', room)}
 * >
 *   {(selectedRoom) => (
 *     <ChatArea room={selectedRoom} />
 *   )}
 * </ConversationsSidebarContainer>
 */

interface ConversationsSidebarContainerProps {
  rooms: ChatRoom[];
  onRoomSelect?: (room: ChatRoom) => void;
  onClearSelection?: (clearFn: () => void) => void; // Callback to register the clear function
  onRegisterSelectFn?: (selectFn: (room: ChatRoom) => void) => void; // Callback to register the select function
  isLoading?: boolean;
  children?: ReactNode | ((selectedRoom: ChatRoom | null) => ReactNode);
  defaultSelectedRoomKey?: string | null;
}

const ConversationsSidebarContainer: FC<ConversationsSidebarContainerProps> = ({
  rooms,
  onRoomSelect,
  onClearSelection,
  onRegisterSelectFn,
  isLoading = false,
  children,
  defaultSelectedRoomKey = null,
}) => {
  const navigate = useNavigate();
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(() => {
    if (defaultSelectedRoomKey) {
      return rooms.find(r => r.session_key === defaultSelectedRoomKey) || null;
    }
    return null;
  });

  const [selectedType, setSelectedType] = useState<RoomTypeKey>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const handleRoomSelect = useCallback((room: ChatRoom) => {
    setSelectedRoom(room);
    if (onRoomSelect) {
      onRoomSelect(room);
    }
  }, [onRoomSelect]);

  // Register the clear selection function with the parent
  React.useEffect(() => {
    if (onClearSelection) {
      onClearSelection(() => {
        console.log("🔄 Clearing selection in ConversationsSidebarContainer");
        setSelectedRoom(null);
      });
    }
  }, [onClearSelection]);

  // Register the select function with the parent
  React.useEffect(() => {
    if (onRegisterSelectFn) {
      onRegisterSelectFn(handleRoomSelect);
    }
  }, [onRegisterSelectFn, handleRoomSelect]);

  const filteredRooms = useMemo(() => {
    let filtered = rooms;

    if (selectedType !== "all" && selectedType !== "unread") {
      filtered = filtered.filter((r) => mapSessionTypeToUiType(r.session_type_id) === selectedType);
    }

    if (selectedType === "unread") {
      filtered = filtered.filter((r) => (r.unread_count || 0) > 0);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((r) => {
        const title = getDisplayNameFromSessionKey(
          r.session_key,
          mapSessionTypeToUiType(r.session_type_id),
          r?.agent,
          r?.client,
          r?.participants
        ).toLowerCase();
        return title.includes(query) || String(r.session_key).toLowerCase().includes(query);
      });
    }

    return [...filtered].sort((a, b) => Number(b.created_at) - Number(a.created_at));
  }, [rooms, selectedType, searchQuery]);

  return (
    <div className="flex h-full w-full">
      {/* Sidebar Panel */}
      <div className="hidden md:flex md:fixed md:left-20 md:top-16 md:bottom-0 md:w-80 bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 shadow-2xl shadow-black/20 dark:shadow-black/40 border-r border-gray-200 dark:border-gray-700 flex-col z-20 backdrop-blur-sm">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>

        <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-bold text-gray-900 dark:text-white bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Conversations for {Global.username}
            </h2>
            <button
              onClick={() => navigate("/agentList")}
              className="p-1.5 text-gray-500 hover:text-white dark:text-gray-400 dark:hover:text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-500 rounded-lg transition-all duration-200 transform hover:scale-105"
            >
              <HiPlus className="w-4 h-4" />
            </button>
          </div>

          <div className="relative mb-2">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none z-10">
              <HiSearch className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm hover:shadow-md transition-all duration-200 backdrop-blur-sm"
              placeholder="Search..."
            />
          </div>

          <div className="flex gap-1 justify-between">
            {Object.entries(ROOM_TYPES).map(([type, config]) => {
              const IconComponent = config.icon;
              const isActive = selectedType === (type as RoomTypeKey);

              return (
                <div key={type} className="relative group">
                  <button
                    onClick={() => setSelectedType(type as RoomTypeKey)}
                    className={`
                      relative flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 transform hover:scale-110
                      ${
                        isActive
                          ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md shadow-blue-500/25"
                          : "bg-white/80 dark:bg-gray-700/80 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 backdrop-blur-sm"
                      }
                    `}
                    title={config.label}
                  >
                    <IconComponent className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          {isLoading && (
            <div className="mt-2 text-[11px] text-gray-500">Loading...</div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
          {isLoading ? (
            <div className="p-3 text-sm text-gray-500">Loading rooms...</div>
          ) : filteredRooms.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-4 text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center mb-3">
                <HiViewGrid className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                No conversations
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {searchQuery ? "No results found" : "Start a new conversation"}
              </p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {filteredRooms.map((room, idx) => {
                const isActive = selectedRoom?.session_key === room.session_key;

                return (
                  <RoomRow
                    key={room.session_key}
                    room={room}
                    index={idx}
                    isActive={isActive}
                    onClick={() => handleRoomSelect(room)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Chat Area (children) */}
      <div className="w-full md:ml-[400px] flex-1 min-w-0 overflow-hidden">
        <div className="h-full w-full">
          {typeof children === "function" ? children(selectedRoom) : children}
        </div>
      </div>
    </div>
  );
};

export default ConversationsSidebarContainer;
